import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Input, Form, Modal, Typography, Space } from "antd";

import CustomSelect from "@/components/inputs/CustomSelect";

import {
  createFile,
  updateFile,
  getFoldersList,
  createFileLink,
} from "@/api/files";
import {
  addFile as addTaskFile,
  addFileLink as addTaskFileLink,
} from "@/api/tasks";
import {
  addFile as addTemplateFile,
  addFileLink as addTemplateFileLink,
} from "@/api/task-templates";

const { Text } = Typography;

// Окно для создания/редактирования файла, содержит форму
const FileModal = ({
  data = {},
  visible,
  onSave,
  onCancel,
  units = [],
  disableUnit = false,
  disableFolder = false,
  skipSave = false,
  beforeSave,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const location = useLocation();
  const isAdmins = location.pathname.startsWith("/administration");

  const [foldersOptions, setFoldersOptions] = useState([]);

  const isNew = !data?.created_at;

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        fileInputRef.current?.focus();
      }, 0);
    }
  }, [visible]);

  const fetchFoldersList = async (unit_uuid) => {
    try {
      const res = await getFoldersList(
        {
          units_uuids: [unit_uuid],
        },
        isAdmins,
      );

      const options = res
        .filter(({ uuid }) => uuid !== data?.uuid)
        .map((item) => {
          const fullPath = item.fullPath
            ? `Файлы/${item.fullPath}/${item.name}`
            : `Файлы/${item.name}`;

          return {
            ...item,
            label: item.name,
            display_label: fullPath,
            value: item.uuid,
          };
        });

      setFoldersOptions(options);
    } catch (error) {
      if (data?.folder) {
        const fullPath = data.folder.fullPath
          ? `Файлы/${data.folder.fullPath}/${data.folder.name}`
          : `Файлы/${data.folder.name}`;

        setFoldersOptions([
          {
            ...data.folder,
            label: data.folder.name,
            display_label: fullPath,
            value: data.folder.uuid,
          },
        ]);
      } else {
        form.setFieldValue("folder_uuid", null);
      }
    }
  };

  useEffect(() => {
    if (data.unit_uuid) {
      // Если unit_uuid уже задан в data, выполняем запрос при инициализации
      fetchFoldersList(data.unit_uuid);
    }
    // eslint-disable-next-line
  }, []);

  const onValuesChange = async (changedValues) => {
    // Проверяем, изменилось ли РГ
    if ("unit_uuid" in changedValues) {
      // сбрасываем родительскую папку
      form.setFieldsValue({
        folder_uuid: undefined,
      });
      // Запрашиваем список новых папок
      fetchFoldersList(form.getFieldValue("unit_uuid"));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    let payload = { ...data, ...values };
    let res;

    try {
      if (beforeSave) {
        payload = await beforeSave(payload);
      }

      if (skipSave) {
        await onSave(payload);
        form.resetFields();
        return;
      }

      if (isNew) {
        if (data.folder?.uuid?.length < 36) {
          delete payload.folder_uuid;
        }

        if (data.isSystem) {
          if (data.additional) {
            if (data.template) {
              res = await addTemplateFileLink(payload);
            } else {
              res = await addTaskFileLink(payload);
            }
          } else {
            res = await createFileLink(payload);
          }
        } else if (data.additional) {
          if (data.template) {
            res = await addTemplateFile(payload);
          } else {
            res = await addTaskFile(payload);
          }
        } else {
          delete payload.task_uuid;
          delete payload.template_uuid;
          res = await createFile(payload);
        }
      } else {
        res = await updateFile(payload);
      }

      if (res?.shortcut?.uuid) {
        onSave(res?.shortcut?.uuid);
      } else if (res) {
        onSave(res?.file || res?.item || { ...payload, ...res });
      } else {
        onSave(payload);
      }

      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  return (
    <Modal
      maskClosable={false}
      title={isNew ? "Загрузка файла" : "Редактирование файла"}
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText={isNew ? "Загрузить" : "Сохранить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
      //width={700}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={data}
        onFinish={handleSave}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          name="name"
          label="Имя файла"
          rules={[{ required: true, message: "Пожалуйста, введите название" }]}
        >
          <Space.Compact className="w-100">
            <Form.Item name="name" noStyle>
              <Input style={{ borderRadius: "4px 0 0 4px" }} />
            </Form.Item>

            <span className="input-after">.{data?.extension}</span>
          </Space.Compact>
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input.TextArea />
        </Form.Item>

        {!skipSave && (
          <>
            <Form.Item
              name="unit_uuid"
              label="Рабочая группа"
              rules={[
                { required: true, message: "Пожалуйста, выберите рабочую группу" },
              ]}
            >
              <CustomSelect
                placeholder="Начните вводить"
                options={units}
                fieldNames={{
                  label: "name",
                  value: "uuid",
                  key: "uuid",
                }}
                showSearch
                optionFilterProp="name"
                disabled={disableUnit || !isNew}
              />
            </Form.Item>

            <Form.Item
              name="folder_uuid"
              label="Расположение"
              rules={[{ required: true, message: "Пожалуйста, выберите папку" }]}
            >
              <CustomSelect
                placeholder="Начните вводить"
                options={foldersOptions}
                disabled={disableFolder}
                showSearch
                allowClear
                optionFilterProp="label"
                optionLabelProp="display_label"
                ref={fileInputRef}
                optionRender={(option) => (
                  <>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {option.data.label}
                    </div>
                    <div style={{ fontSize: 9, color: "#999" }}>
                      {option.data.display_label}
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </>
        )}

        {error && (
          <div>
            <Text type="danger">{error}</Text>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default FileModal;
